// ABOUTME: Reports the one attention signal the server cannot see — dwell:
// ABOUTME: visibility- and idle-gated linger per entity, batched to /me/attention/events.
import { type Ref, computed, onScopeDispose, watch } from "vue";

import { useServices } from "@/lib/openapi";

/** No pointer/key/scroll for this long ⇒ the person is not actually reading. */
export const IDLE_CUTOFF_MS = 30_000;
/** A visit shorter than this is a bounce — never reported at all. */
export const MIN_REPORT_MS = 5_000;
/** Per-visit ceiling; past this, "still on the page" is a parked tab. */
export const VISIT_CAP_MS = 10 * 60 * 1000;
/** Cadence of ordinary batched flushes while a visit is ongoing. */
export const FLUSH_INTERVAL_MS = 20_000;

const ACTIVITY_EVENTS = [
    "pointermove",
    "pointerdown",
    "keydown",
    "wheel",
    "scroll",
    "touchstart",
] as const;

export interface AttentionEvent {
    entity_ref: string;
    kind: "dwell" | "element_focused" | "edited";
    dwell_ms?: number;
}

export type SendEvents = (
    events: AttentionEvent[],
    opts: { keepalive: boolean }
) => Promise<void> | void;

interface AttentionProbeOptions {
    /** Canonical `{type}:{id}` of what the person is looking at, null between pages. */
    entityRef: Ref<string | null | undefined>;
    /** Both off-switches resolved (master AND probe) — false ⇒ fully inert. */
    enabled: Ref<boolean>;
    /** Injectable transport; the default posts through the generated client. */
    send?: SendEvents;
}

/**
 * The dwell probe. Fire-and-forget by design: it never throws, never retries,
 * and reports only what the server cannot infer — the server keeps inferring
 * visits from `/info` on its own. When `enabled` is false nothing is measured,
 * nothing is sent, and no listener does any work.
 */
export const useAttentionProbe = (options: AttentionProbeOptions) => {
    const api = useServices({
        opts: { throwOnError: false },
        requires: { auth: true, organization: true, stek: false },
    });

    const send: SendEvents =
        options.send ??
        (async (events, { keepalive }) => {
            try {
                await api.recordAttentionEventsMeAttentionEventsPost({
                    body: { events },
                    // Part of RequestInit (the SDK options extend it): lets the
                    // pagehide flush outlive the page. `sendBeacon` cannot carry
                    // the Authorization header, keepalive-fetch can.
                    keepalive,
                });
            } catch {
                // Losing a dwell report degrades ranking only.
            }
        });

    const enabled = computed(() => options.enabled.value === true);

    // ---- visit accounting ---------------------------------------------------
    let segmentStart: number | null = null;
    let pendingMs = 0; // accrued but not yet flushed, for the current entity
    let visitMs = 0; // total accrued this visit (drives MIN/CAP)
    let currentRef: string | null = null;
    let idle = false;
    let idleTimer: ReturnType<typeof setTimeout> | undefined;
    let flushTimer: ReturnType<typeof setInterval> | undefined;
    const queue: AttentionEvent[] = [];

    const isVisible = () =>
        typeof document === "undefined" ||
        document.visibilityState === "visible";

    const shouldAccrue = () =>
        enabled.value &&
        currentRef !== null &&
        isVisible() &&
        !idle &&
        visitMs < VISIT_CAP_MS;

    const openSegment = () => {
        if (segmentStart === null && shouldAccrue()) {
            segmentStart = Date.now();
        }
    };

    const closeSegment = () => {
        if (segmentStart === null) {
            return;
        }
        const elapsed = Date.now() - segmentStart;
        segmentStart = null;
        const room = Math.max(0, VISIT_CAP_MS - visitMs);
        const credited = Math.min(Math.max(0, elapsed), room);
        pendingMs += credited;
        visitMs += credited;
    };

    /** Move the visit's pending ms into the queue — once the visit is real. */
    const queuePending = () => {
        if (currentRef !== null && pendingMs > 0 && visitMs >= MIN_REPORT_MS) {
            queue.push({
                entity_ref: currentRef,
                kind: "dwell",
                dwell_ms: Math.round(pendingMs),
            });
            pendingMs = 0;
        }
    };

    const drainQueue = (keepalive: boolean) => {
        if (queue.length === 0) {
            return;
        }
        const batch = queue.splice(0, queue.length);
        void send(batch, { keepalive });
    };

    const flush = (keepalive = false) => {
        closeSegment();
        queuePending();
        drainQueue(keepalive);
        openSegment();
    };

    /** A live-mode element selection — deliberate, and invisible to the server. */
    const reportElementFocus = (elementRef: string) => {
        if (!enabled.value) {
            return;
        }
        queue.push({ entity_ref: elementRef, kind: "element_focused" });
    };

    /** A switch of an entity into edit mode — the same: client state only. */
    const reportEdit = (entityRef: string) => {
        if (!enabled.value) {
            return;
        }
        queue.push({ entity_ref: entityRef, kind: "edited" });
    };

    // ---- signal sources -----------------------------------------------------
    const onActivity = () => {
        if (!enabled.value) {
            return;
        }
        if (idle) {
            idle = false;
            openSegment();
        }
        clearTimeout(idleTimer);
        idleTimer = setTimeout(() => {
            idle = true;
            closeSegment();
        }, IDLE_CUTOFF_MS);
    };

    const onVisibility = () => {
        if (isVisible()) {
            openSegment();
        } else {
            // Tab went to the background: bank the segment and get it out —
            // the page may never come back.
            flush();
        }
    };

    const onPagehide = () => {
        // A bounce (< MIN_REPORT_MS) is deliberately dropped here too.
        flush(true);
    };

    watch(
        () => options.entityRef.value ?? null,
        (next) => {
            if (!enabled.value) {
                currentRef = next;
                return;
            }
            // Settle the old visit first; its remainder is dropped if it was a
            // bounce, then the counters restart for the new entity.
            closeSegment();
            queuePending();
            drainQueue(false);
            pendingMs = 0;
            visitMs = 0;
            currentRef = next;
            openSegment();
        },
        { immediate: true }
    );

    const listeners: Array<[string, EventListener]> = [];
    const attach = () => {
        if (typeof window === "undefined") {
            return;
        }
        for (const name of ACTIVITY_EVENTS) {
            const handler = onActivity as EventListener;
            window.addEventListener(name, handler, { passive: true });
            listeners.push([name, handler]);
        }
        document.addEventListener("visibilitychange", onVisibility);
        listeners.push(["visibilitychange", onVisibility as EventListener]);
        window.addEventListener("pagehide", onPagehide);
        listeners.push(["pagehide", onPagehide as EventListener]);
        flushTimer = setInterval(() => flush(), FLUSH_INTERVAL_MS);
        onActivity();
        openSegment();
    };

    const detach = () => {
        for (const [name, handler] of listeners.splice(0, listeners.length)) {
            if (name === "visibilitychange") {
                document.removeEventListener(name, handler);
            } else {
                window.removeEventListener(name, handler);
            }
        }
        clearInterval(flushTimer);
        clearTimeout(idleTimer);
    };

    watch(
        enabled,
        (on) => {
            if (on) {
                attach();
            } else {
                // Off means off: stop measuring and drop what was unmeasured.
                detach();
                segmentStart = null;
                pendingMs = 0;
                visitMs = 0;
                queue.length = 0;
            }
        },
        { immediate: true }
    );

    onScopeDispose(() => {
        if (enabled.value) {
            flush(true);
        }
        detach();
    });

    return { flush, reportElementFocus, reportEdit };
};
